/**
 * SettingsPage — Ajustes. Credenciales de Zicasso Partners por agente.
 * Cada agente guarda su email+contraseña de Zicasso (cifrada) para que los
 * mensajes salgan con su propio perfil. Los mensajes de un lead se envían con
 * las credenciales del agente ASIGNADO al lead.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Settings, ShieldCheck, ShieldAlert, KeyRound, CheckCircle2, RotateCw,
  Trash2, Lock, AlertTriangle, MessageSquareText, Plus, Pencil,
} from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader,
} from "@/components/ui/dialog";

const initials = (n = "") =>
  n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return format(new Date(iso), "d MMM yyyy · HH:mm", { locale: es }); } catch { return "—"; }
};

function CredentialDialog({ open, onClose, row }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");

  React.useEffect(() => { if (open) setPassword(""); }, [open, row]);

  const saveMut = useMutation({
    mutationFn: () => api.post("/zicasso/credentials", { email: row.email, password }),
    onSuccess: (res) => {
      toast.success(`Credenciales verificadas — ${res.data?.display_name || row.name}`);
      qc.invalidateQueries({ queryKey: ["zicasso-credentials"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo verificar el login en Zicasso")),
  });

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="credential-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Credenciales de Zicasso — {row.name}
          </DialogTitle>
          <DialogDescription>
            Introduce la contraseña de <span className="font-medium">{row.email}</span> en
            Zicasso Partners. Se probará el login y se guardará <span className="font-medium">cifrada</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Email de Zicasso Partners</Label>
            <Input value={row.email} disabled className="mt-1 bg-secondary/50" data-testid="credential-email-input" />
          </div>
          <div>
            <Label className="text-xs">Contraseña de Zicasso Partners</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && password.trim()) saveMut.mutate(); }}
              data-testid="credential-password-input"
            />
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            La contraseña se cifra en el servidor y nunca se muestra. Solo se usa para
            enviar mensajes a Zicasso con este perfil.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="credential-cancel-button">Cancelar</Button>
          <Button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !password.trim()}
            data-testid="credential-save-button"
          >
            {saveMut.isPending ? "Verificando…" : "Verificar y guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredentialRow({ row }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const verifyMut = useMutation({
    mutationFn: () => api.post(`/zicasso/credentials/${encodeURIComponent(row.email)}/verify`),
    onSuccess: (res) => {
      toast.success(`Verificado — ${res.data?.display_name || row.name}`);
      qc.invalidateQueries({ queryKey: ["zicasso-credentials"] });
    },
    onError: (e) => {
      toast.error(apiErrorMessage(e, "Verificación fallida"));
      qc.invalidateQueries({ queryKey: ["zicasso-credentials"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/zicasso/credentials/${encodeURIComponent(row.email)}`),
    onSuccess: () => {
      toast.success("Credenciales eliminadas");
      qc.invalidateQueries({ queryKey: ["zicasso-credentials"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo eliminar")),
  });

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-xl border border-black/5 bg-card p-4 shadow-sm"
      data-testid={`credential-row-${row.email}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {initials(row.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold">{row.name}</span>
          {row.is_self && <Badge variant="outline" className="h-4 text-[9px]">Tú</Badge>}
        </div>
        <div className="truncate text-[11px] text-muted-foreground">{row.email}</div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        {row.verified ? (
          <Badge className="gap-1 bg-[#E2EBE5] text-[#244532] hover:bg-[#E2EBE5]" data-testid={`credential-status-${row.email}`}>
            <CheckCircle2 className="h-3 w-3" /> Verificado
          </Badge>
        ) : row.configured ? (
          <Badge variant="outline" className="gap-1 border-[#C28F5B]/40 bg-[#F1E9DA] text-[#6B4F20]" data-testid={`credential-status-${row.email}`}>
            <AlertTriangle className="h-3 w-3" /> Sin verificar
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid={`credential-status-${row.email}`}>
            Sin configurar
          </Badge>
        )}
        {row.configured && (
          <span className="text-[10px] text-muted-foreground">Verif.: {fmtDate(row.last_verified_at)}</span>
        )}
      </div>

      {row.can_edit && (
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" variant={row.configured ? "outline" : "default"} onClick={() => setDialogOpen(true)} data-testid={`configure-credential-${row.email}`}>
            <KeyRound className="mr-1 h-3 w-3" /> {row.configured ? "Cambiar" : "Configurar"}
          </Button>
          {row.configured && (
            <>
              <Button size="sm" variant="ghost" onClick={() => verifyMut.mutate()} disabled={verifyMut.isPending} title="Verificar login" data-testid={`verify-credential-${row.email}`}>
                <RotateCw className={`h-3.5 w-3.5 ${verifyMut.isPending ? "animate-spin" : ""}`} />
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={() => { if (window.confirm(`¿Eliminar las credenciales de ${row.name}?`)) deleteMut.mutate(); }}
                disabled={deleteMut.isPending}
                title="Eliminar" className="text-[#C45A45]"
                data-testid={`delete-credential-${row.email}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      )}

      <CredentialDialog open={dialogOpen} onClose={() => setDialogOpen(false)} row={row} />
    </div>
  );
}

const SMS_PLACEHOLDER_HELP =
  "Variables: {{name}} (nombre del viajero), {{agent}} (tu nombre), {{destination}} (países), {{phone}} (teléfono de empresa), {{ext}} (tu extensión).";

function SmsTemplateDialog({ open, onClose, tpl }) {
  const qc = useQueryClient();
  const editing = !!tpl?.id;
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  React.useEffect(() => { if (open) { setName(tpl?.name || ""); setBody(tpl?.body || ""); } }, [open, tpl]);

  const save = useMutation({
    mutationFn: () => (editing
      ? api.put(`/zicasso/sms-templates/${tpl.id}`, { name, body })
      : api.post("/zicasso/sms-templates", { name, body })),
    onSuccess: () => {
      toast.success(editing ? "Plantilla actualizada" : "Plantilla creada");
      qc.invalidateQueries({ queryKey: ["sms-templates"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo guardar la plantilla")),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sms-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4" /> {editing ? "Editar plantilla de SMS" : "Nueva plantilla de SMS"}
          </DialogTitle>
          <DialogDescription>{SMS_PLACEHOLDER_HELP}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs">Nombre de la plantilla</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Seguimiento propuesta" className="mt-1" autoFocus data-testid="sms-template-name-input" />
          </div>
          <div>
            <Label className="text-xs">Texto del SMS</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Hi {{name}}, this is {{agent}} from Espíritu Travel…" className="mt-1" data-testid="sms-template-body-input" />
            <p className={`mt-1 text-[11px] ${body.length > 640 ? "text-[#C45A45]" : "text-muted-foreground"}`}>{body.length}/640</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="sms-template-cancel">Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !body.trim() || body.length > 640} data-testid="sms-template-save">
            {save.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SmsTemplatesSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["sms-templates"],
    queryFn: async () => (await api.get("/zicasso/sms-templates")).data,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = useMutation({
    mutationFn: (id) => api.delete(`/zicasso/sms-templates/${id}`),
    onSuccess: () => { toast.success("Plantilla eliminada"); qc.invalidateQueries({ queryKey: ["sms-templates"] }); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo eliminar")),
  });

  return (
    <div className="mt-10" data-testid="sms-templates-section">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <MessageSquareText className="h-5 w-5 text-primary" /> Plantillas de SMS
        </h2>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="sms-template-new-button">
          <Plus className="mr-1 h-4 w-4" /> Nueva plantilla
        </Button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Textos cortos para la herramienta <span className="font-medium">Enviar SMS</span>. {SMS_PLACEHOLDER_HELP}
      </p>

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
      ) : (
        <div className="space-y-2" data-testid="sms-templates-list">
          {(data || []).map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-xl border border-black/5 bg-card p-3.5 shadow-sm" data-testid={`sms-template-row-${t.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{t.name}</p>
                <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{t.body}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(t); setDialogOpen(true); }} title="Editar" data-testid={`sms-template-edit-${t.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[#C45A45]" onClick={() => { if (window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) del.mutate(t.id); }} title="Eliminar" data-testid={`sms-template-delete-${t.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {(data || []).length === 0 && (
            <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              Aún no hay plantillas de SMS. Crea la primera con "Nueva plantilla".
            </p>
          )}
        </div>
      )}

      <SmsTemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} tpl={editing} />
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["zicasso-credentials"],
    queryFn: async () => (await api.get("/zicasso/credentials")).data,
  });

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 md:px-8" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold tracking-tight">
          <Settings className="h-7 w-7 text-primary" /> Ajustes
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Credenciales de conexión con Zicasso Partners por agente.
        </p>
      </div>

      <Card className="mb-5 border-primary/10 bg-primary/5">
        <CardContent className="flex items-start gap-3 p-4">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-semibold">Envío con el perfil del agente asignado</p>
            <p className="mt-0.5 text-muted-foreground">
              Cuando se envía un mensaje a un lead, sale con las credenciales de Zicasso del
              agente <span className="font-medium">asignado a ese lead</span>. Cada agente debe
              configurar aquí sus credenciales; si no, el envío quedará bloqueado.
            </p>
          </div>
        </CardContent>
      </Card>

      {data && !data.live_actions_enabled && (
        <Card className="mb-5 border-[#C28F5B]/30 bg-[#F1E9DA]">
          <CardContent className="flex items-start gap-3 p-4 text-sm text-[#6B4F20]">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Envíos reales desactivados</p>
              <p className="mt-0.5">Los mensajes se registran en local (no se envían a Zicasso) porque
                <span className="font-mono"> ZICASSO_LIVE_ACTIONS_ENABLED=false</span>.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <h2 className="mb-3 text-lg font-semibold">Credenciales de Zicasso Partners</h2>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-2" data-testid="credentials-list">
          {(data?.items || []).map((row) => <CredentialRow key={row.email} row={row} />)}
          {(data?.items || []).length === 0 && (
            <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No hay agentes disponibles.
            </p>
          )}
        </div>
      )}

      {!user?.is_admin && (
        <p className="mt-4 text-xs text-muted-foreground">
          Solo puedes gestionar tus propias credenciales. Un administrador puede configurar las del resto del equipo.
        </p>
      )}

      <SmsTemplatesSection />
    </div>
  );
}
